import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabase";
import { useAuth } from "../contexts/AuthContext";
import TokenPurchaseModal from "../components/TokenPurchaseModal";

// Helper function to get category name from ID
const getCategoryName = (categoryId) => {
  const categories = {
    "clinical-medicine": "Clinical Medicine",
    "medical-education": "Medical Education",
    "medical-communication": "Medical Communication",
    "pharmaceuticals": "Pharmaceuticals",
    "clinical-research": "Clinical Research",
    "healthtech": "HealthTech",
    "business-consulting": "Business Consulting",
    "entrepreneurship": "Entrepreneurship",
    "policy-legal-ethics": "Policy/Legal/Ethics",
    "marketing-sales": "Marketing/Sales",
    "creative": "Creative",
    "wellness-lifestyle": "Wellness/Lifestyle"
  };
  
  return categories[categoryId] || categoryId;
};

// Format date function
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// Collapsible section component
const CollapsibleSection = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="py-2">
      <button 
        className="w-full flex items-center justify-between text-left bg-white border border-gray-200 rounded-none px-4 py-3 font-medium text-gray-900 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
        style={{ boxShadow: 'none' }}
      >
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          viewBox="0 0 20 20" 
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {isOpen && (
        <div className="mt-2 text-gray-700 px-4">
          {children}
        </div>
      )}
    </div>
  );
};

// Progress bar component
const ProgressBar = ({ score, max, label, percentage }) => {
  const calculatedPercentage = percentage || (max > 0 ? (score / max) * 100 : 0);
  const barColor = calculatedPercentage < 50 ? 'bg-black' : 'bg-green-600';
  
  return (
    <div className="mb-4">
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}:</span>
        <span className="text-sm font-medium text-gray-700">{score}/{max} ({Math.round(calculatedPercentage)}%)</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div 
          className={`${barColor} h-3 rounded-full`}
          style={{ width: `${calculatedPercentage}%` }}
        ></div>
      </div>
    </div>
  );
};

// Circle progress chart component
const CircleProgressChart = ({ percentage }) => {
  // SVG parameters
  const size = 240;
  const strokeWidth = 30;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const dash = (percentage * circumference) / 100;
  const progressColor = percentage < 50 ? '#000000' : '#4ade80';
  
  return (
    <div className="relative flex items-center justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - dash}
          strokeLinecap="round"
        />
      </svg>
      
      {/* Percentage text in the middle */}
      <div className="absolute text-center">
        <span className="text-6xl font-bold">{percentage}</span>
        <span className="text-4xl font-bold">%</span>
        <p className="text-xl font-medium mt-2">Overall score</p>
      </div>
    </div>
  );
};

// Analysis history item component
const AnalysisHistoryItem = ({ analysis, isSelected, onClick }) => {
  // Get the results - handle both array and object formats
  const results = Array.isArray(analysis.cv_analysis_results) && analysis.cv_analysis_results.length > 0 
    ? analysis.cv_analysis_results[0] 
    : analysis.cv_analysis_results;

  return (
    <button 
      onClick={() => onClick(analysis.id)}
      className={`w-full text-left p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
    >
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="inline-block bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
            {getCategoryName(analysis.job_category)}
          </div>
          {isSelected && (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <h4 className="font-medium text-sm text-gray-900 truncate">{analysis.file_name || 'Unnamed CV'}</h4>
        <div className="text-xs text-gray-500">
          {formatDate(analysis.created_at)}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600">Score:</span>
          <span className="text-sm font-medium text-gray-900">{results?.overall_percentage || 'N/A'}%</span>
        </div>
      </div>
    </button>
  );
};

const CVAnalysisResultsPage = ({ analysisId: propAnalysisId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [analyses, setAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 1536);
  const [showTokenModal, setShowTokenModal] = useState(false);
  
  // Extract analysis ID from URL if present, or use prop
  const queryParams = new URLSearchParams(location.search);
  const analysisIdFromUrl = queryParams.get('id');
  const analysisId = propAnalysisId || analysisIdFromUrl;

  useEffect(() => {
    // Debug console logs
    console.log("Auth state check:", { 
      userExists: !!user,
      userId: user?.id,
      authChecked
    });

    // Give auth a moment to initialize
    const checkAuth = async () => {
      // Check if user exists in context
      if (!user) {
        // Double-check with Supabase directly
        const { data: { user: supabaseUser } } = await supabase.auth.getUser();
        
        console.log("Supabase getUser result:", supabaseUser);
        
        if (supabaseUser) {
          // User is authenticated in Supabase but not in context
          console.log("User authenticated in Supabase but not in context:", supabaseUser.id);
          setAuthChecked(true);
          // The useAuth hook should update with the user soon
          return;
        }
        
        // Only redirect if we're sure the user isn't authenticated
        console.log("User not authenticated, redirecting to login");
        navigate("/login", { 
          state: { from: location.pathname + location.search } 
        });
        return;
      }
      
      setAuthChecked(true);
    };
    
    // Small delay to ensure auth is initialized
    const timer = setTimeout(() => {
      checkAuth();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [user, navigate, location]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1536px)");
    const handleChange = (e) => setIsLargeScreen(e.matches);

    // For older browsers, use 'addListener'
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Second useEffect to fetch data once auth is confirmed
  useEffect(() => {
    if (!authChecked || !user) {
      return; // Wait until auth is checked and user exists
    }

    // Fetch analyses for the authenticated user
    const fetchUserAnalyses = async () => {
      try {
        console.log("Fetching analyses for user:", user.id);
        setLoading(true);
        
        // Get all analyses for the user
        const { data, error } = await supabase
          .from('cv_analysis_requests')
          .select('*, cv_analysis_results(*)')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error("Supabase query error:", error);
          throw error;
        }
        
        console.log("User analyses fetched:", data?.length || 0);
        
        // Filter analyses that have results
        const completedAnalyses = data.filter(analysis => 
          analysis.cv_analysis_results && 
          (Array.isArray(analysis.cv_analysis_results) ? 
            analysis.cv_analysis_results.length > 0 : 
            Object.keys(analysis.cv_analysis_results).length > 0)
        );
        
        if (completedAnalyses.length > 0) {
          setAnalyses(completedAnalyses);
          
          // If a specific analysis ID is provided (via prop or URL), select it
          if (analysisId) {
            console.log("Looking for analysis with ID:", analysisId);
            const analysisFromId = completedAnalyses.find(a => a.id === analysisId);
            if (analysisFromId) {
              console.log("Found analysis with ID");
              setSelectedAnalysis(analysisFromId);
            } else {
              console.log("Analysis ID not found, using most recent");
              setSelectedAnalysis(completedAnalyses[0]);
            }
          } else {
            // Otherwise select the most recent
            console.log("No analysis ID provided, using most recent");
            setSelectedAnalysis(completedAnalyses[0]);
          }
          setError(null);
        } else {
          // No completed analyses found
          console.log("No completed analyses found for user");
          setError("No completed CV analyses found. Please upload a CV for analysis.");
        }
      } catch (err) {
        console.error("Error fetching CV analyses:", err);
        setError("Failed to load your CV analyses. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserAnalyses();
  }, [user, authChecked, analysisId]);

  // Function to handle selecting a different analysis
  const handleSelectAnalysis = (analysisId) => {
    console.log("Selecting analysis:", analysisId);
    const analysis = analyses.find(a => a.id === analysisId);
    if (analysis) {
      setSelectedAnalysis(analysis);
      
      // Update URL to reflect selected analysis
      const newUrl = `${window.location.pathname}?id=${analysisId}`;
      window.history.pushState({}, '', newUrl);
    }
  };

  // Parse JSON arrays if they're stored as strings
  const parseJsonArray = (jsonString) => {
    if (!jsonString) return [];
    
    try {
      if (typeof jsonString === 'string') {
        return JSON.parse(jsonString);
      }
      return Array.isArray(jsonString) ? jsonString : [jsonString];
    } catch (e) {
      console.error("Error parsing JSON array:", e);
      return Array.isArray(jsonString) ? jsonString : [jsonString];
    }
  };

  // Placeholder UI until real analysis is ready
  if (loading) {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-medium text-gray-900 mb-2">Loading your CV analysis...</h2>
            <p className="text-gray-500">Please wait while we retrieve your results.</p>
          </div>
        </div>
    );
  }

  if (error) {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-medium text-gray-900 mb-2">Analysis Error</h2>
            <p className="text-gray-700 mb-4">{error}</p>
            <button 
              onClick={() => navigate("/cv-analyser")}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              Upload a CV
            </button>
          </div>
        </div>
    );
  }

  if (!selectedAnalysis || !selectedAnalysis.cv_analysis_results) {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-yellow-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-xl font-medium text-gray-900 mb-2">No Analysis Results</h2>
            <p className="text-gray-700 mb-4">We couldn't find any completed analysis results for your CV. Please upload your CV for analysis.</p>
            <button 
              onClick={() => navigate("/cv-analyser")}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              Upload CV
            </button>
          </div>
        </div>
    );
  }

  // Get the results - handle both array and object formats
  const results = Array.isArray(selectedAnalysis.cv_analysis_results) && selectedAnalysis.cv_analysis_results.length > 0 
    ? selectedAnalysis.cv_analysis_results[0] 
    : selectedAnalysis.cv_analysis_results;

  // Parse the full_analysis JSON if it exists
  let fullAnalysis = {};
  try {
    if (results.full_analysis) {
      fullAnalysis = typeof results.full_analysis === 'string' 
        ? JSON.parse(results.full_analysis) 
        : results.full_analysis;
    }
  } catch (e) {
    console.error("Error parsing full_analysis:", e);
  }

  // Parse recommendations arrays
  const strengtheningApplication = parseJsonArray(results.strengthening_application);
  const professionalDevelopment = parseJsonArray(results.professional_development);

  // Extract strengths and areas for improvement from the new format
  const strengths = results.strengths ? results.strengths.split('\n•').filter(Boolean).map(s => s.trim().replace(/^•\s*/, '')) : [];
  const areasForImprovement = results.areas_for_improvement ? results.areas_for_improvement.split('\n•').filter(Boolean).map(s => s.trim().replace(/^•\s*/, '')) : [];

  // Convert string numbers to integers for display
  const category1Score = parseInt(results.category_1_score) || 0;
  const category1Max = parseInt(results.category_1_max) || 100;
  
  const category2Score = parseInt(results.category_2_score) || 0;
  const category2Max = parseInt(results.category_2_max) || 100;
  
  const category3Score = parseInt(results.category_3_score) || 0;
  const category3Max = parseInt(results.category_3_max) || 100;
  
  const category4Score = parseInt(results.category_4_score) || 0;
  const category4Max = parseInt(results.category_4_max) || 100;
  
  const category5Score = parseInt(results.category_5_score) || 0;
  const category5Max = parseInt(results.category_5_max) || 100;

  // Use actual overall score and percentage from database if available
  const overallScore = parseInt(results.overall_score) || (category1Score + category2Score + category3Score + category4Score + category5Score);
  const overallMax = parseInt(results.overall_max_score) || (category1Max + category2Max + category3Max + category4Max + category5Max);
  const overallPercentage = results.overall_percentage ? Math.round(parseFloat(results.overall_percentage)) : Math.round((overallScore/overallMax)*100);

  // Main results view
  return (
    <div className={`${isLargeScreen ? "max-w-7xl mx-auto px-8" : "max-w-full px-4 sm:px-6 lg:px-8" } py-8`}>
        {/* Header with category badge and buttons */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div className="flex-1">
            <div className="inline-block bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full mb-2">
              {getCategoryName(results.job_category)}
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold">Resume Analyser results</h1>
            <p className="text-sm text-gray-500 mt-1">
              Analysed on {formatDate(selectedAnalysis.created_at)}
            </p>
        </div>
        <div className="flex flex-nowrap gap-2 md:gap-3 mt-4 md:mt-0">
          {analyses.length > 1 && (
            <button 
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center px-3 md:px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              {showHistory ? 'Hide history' : 'View history'}
            </button>
          )}
          
          <button 
            onClick={() => navigate("/cv-analyser")}
            className="flex items-center px-3 md:px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Analyse new CV
          </button>
        </div>
      </div>
      
      {/* Main layout - conditionally show history panel */}
      <div className="grid grid-cols-1 lg:grid-cols-12 w-full gap-6 lg:gap-8">
        {/* History panel - only shown when history button is clicked */}
        {showHistory && (
          <>
            {/* Mobile overlay */}
            <div className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden" onClick={() => setShowHistory(false)}></div>
            
            {/* History panel */}
            <div className="fixed inset-y-0 left-0 w-80 bg-white shadow-xl z-40 lg:relative lg:col-span-3 lg:w-auto lg:shadow-none">
              <div className="bg-white w-full rounded-lg border border-gray-200 shadow-sm overflow-hidden h-full flex flex-col">
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Analysis History</h3>
                  <button
                    onClick={() => setShowHistory(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
                {/* History items and token section - no gaps */}
                <div className="flex-1 overflow-y-auto">
                  {analyses.map(analysis => (
                    <AnalysisHistoryItem 
                      key={analysis.id}
                      analysis={analysis}
                      isSelected={selectedAnalysis.id === analysis.id}
                      onClick={handleSelectAnalysis}
                    />
                  ))}
                  
                  {/* Token Purchase Section - directly after history items */}
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    <h3 className="font-medium mb-2">Need more tokens?</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Purchase tokens to analyze more CVs
                    </p>
                    <button
                      onClick={() => setShowTokenModal(true)}
                      className="w-full px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                    >
                      Purchase Tokens
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Main content area - adjust span based on history visibility */}
        <div className={`${showHistory ? 'lg:col-span-9' : 'lg:col-span-12'}`}>
          {/* Main content - 2 columns on desktop */}
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.5fr] gap-6 lg:gap-8">
            {/* Left column - Circle chart and overall feedback */}
            <div className="bg-white h-max p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="w-full flex justify-center">
                <CircleProgressChart percentage={overallPercentage} />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2 mt-5">Overall feedback</h3>
                <p className="text-gray-700">
                  {results.assessment_report ||
                    "Your CV has been analysed using our comprehensive assessment methodology. Review the detailed feedback below to understand your strengths and areas for improvement."}
                </p>
              </div>
            </div>
            {/* Right column - Category scores and detailed feedback */}
            <div className="p-0">
              <h3 className="text-lg font-bold mb-2 text-left">Category Scores</h3>
              <ProgressBar
                score={category1Score}
                max={category1Max}
                percentage={results.category_1_percentage ? Math.round(parseFloat(results.category_1_percentage)) : Math.round((category1Score / category1Max) * 100)}
                label={results.category_1_name || "Category 1"}
              />
              <ProgressBar
                score={category2Score}
                max={category2Max}
                percentage={results.category_2_percentage ? Math.round(parseFloat(results.category_2_percentage)) : Math.round((category2Score / category2Max) * 100)}
                label={results.category_2_name || "Category 2"}
              />
              <ProgressBar
                score={category3Score}
                max={category3Max}
                percentage={results.category_3_percentage ? Math.round(parseFloat(results.category_3_percentage)) : Math.round((category3Score / category3Max) * 100)}
                label={results.category_3_name || "Category 3"}
              />
              <ProgressBar
                score={category4Score}
                max={category4Max}
                percentage={results.category_4_percentage ? Math.round(parseFloat(results.category_4_percentage)) : Math.round((category4Score / category4Max) * 100)}
                label={results.category_4_name || "Category 4"}
              />
              <ProgressBar
                score={category5Score}
                max={category5Max}
                percentage={results.category_5_percentage ? Math.round(parseFloat(results.category_5_percentage)) : Math.round((category5Score / category5Max) * 100)}
                label={results.category_5_name || "Category 5"}
              />

              {/* Detailed feedback section */}
              <div className="mt-4 mb-4 p-0">
                <h3 className="text-lg font-bold mb-2 text-left">Detailed feedback</h3>

                {/* Strengths section */}
                {strengths.length > 0 && (
                  <CollapsibleSection title="Strengths">
                    <ul className="space-y-2 list-disc pl-5">
                      {strengths.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </CollapsibleSection>
                )}

                {/* Areas for improvement section */}
                {areasForImprovement.length > 0 && (
                  <CollapsibleSection title="Areas for improvement">
                    <ul className="space-y-2 list-disc pl-5">
                      {areasForImprovement.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </CollapsibleSection>
                )}

                {/* Strengthening application section */}
                {strengtheningApplication.length > 0 && (
                  <CollapsibleSection title="Strengthening your application">
                    <ul className="space-y-2 list-disc pl-5">
                      {strengtheningApplication.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </CollapsibleSection>
                )}

                {/* Professional development section */}
                {professionalDevelopment.length > 0 && (
                  <CollapsibleSection title="Professional development & upskilling">
                    <ul className="space-y-2 list-disc pl-5">
                      {professionalDevelopment.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </CollapsibleSection>
                )}

                {/* Fallback content if no specific recommendations */}
                {strengths.length === 0 && areasForImprovement.length === 0 && strengtheningApplication.length === 0 && professionalDevelopment.length === 0 && (
                  <div className="text-center text-gray-500 py-4">
                    <p>Detailed recommendations will appear here once your analysis is complete.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/** Floating action button for token purchase (hidden on all screens)
       *  Previously rendered a fixed button on mobile. Commented out per request.
       */}
      {false && !showHistory && (
        <div className="fixed bottom-6 right-6 lg:hidden z-30">
          <button
            onClick={() => setShowTokenModal(true)}
            className="bg-black text-white p-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors"
            title="Purchase Tokens"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </button>
        </div>
      )}

      {/* Token Purchase Modal */}
      <TokenPurchaseModal
        isOpen={showTokenModal}
        onClose={() => setShowTokenModal(false)}
        onSuccess={() => {
          // Optionally refresh user data or show success message
          console.log("Tokens purchased successfully");
        }}
      />
    </div>
  );
};

export default CVAnalysisResultsPage;